<template>
  <v-dialog
    :value="Boolean(notice)"
    max-width="560"
    persistent
  >
    <v-card data-testid="session_changed_dialog">
      <v-card-title class="headline" data-testid="session_changed_dialog_title">
        {{ $t("session_changed.title") }}
      </v-card-title>
      <v-card-text data-testid="session_changed_dialog_message">
        {{ dialog_message }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          data-testid="session_changed_dialog_reload"
          @click="reload"
        >
          {{ $t("session_changed.reload_button") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import {
  fetchCurrentSession,
  sessionIdentityFromState,
  sessionsMatch,
  sessionSync,
} from "../lib/session_sync";

export default {
  name: "SessionChangedDialog",
  data() {
    return {
      baseline: null,
      baseline_initialized: false,
      latest_message: null,
      notice: null,
      reconcile_revision: 0,
      unsubscribe_session_sync: null,
    };
  },
  computed: {
    ...mapState([
      "app_config",
      "current_user",
      "current_user_loaded",
      "masquerade",
    ]),
    dialog_message() {
      if (!this.notice) return "";
      const before_name = this.identity_name(this.notice.before);
      const after_name = this.identity_name(this.notice.after);
      switch (this.notice.transition) {
        case "login-pending":
          return this.$t("session_changed.login_pending", {
            email: this.notice.pending_login_email || this.$t("session_changed.another_account"),
          });
        case "logout":
          return before_name
            ? this.$t("session_changed.logged_out", { name: before_name })
            : this.$t("session_changed.logged_out_unknown");
        case "session-expired":
          return before_name
            ? this.$t("session_changed.expired", { name: before_name })
            : this.$t("session_changed.expired_unknown");
        case "masquerade":
          return this.$t("session_changed.masqueraded", { name: after_name });
        case "unmasquerade":
          return this.$t("session_changed.unmasqueraded", {
            viewed_name: before_name,
            name: after_name,
          });
        default:
          return this.$t("session_changed.logged_in", { name: after_name });
      }
    },
  },
  watch: {
    current_user_loaded: {
      immediate: true,
      handler(loaded) {
        if (!loaded || this.baseline_initialized) return;
        this.baseline = sessionIdentityFromState({
          current_user: this.current_user,
          masquerade: this.masquerade,
        });
        this.baseline_initialized = true;
        if (this.latest_message) this.reconcile(this.latest_message);
      },
    },
  },
  mounted() {
    this.unsubscribe_session_sync = sessionSync(this.app_config.id).subscribe(
      this.receive_session_change,
    );
    document.addEventListener("visibilitychange", this.handle_visibility_change);
  },
  beforeDestroy() {
    if (this.unsubscribe_session_sync) this.unsubscribe_session_sync();
    document.removeEventListener("visibilitychange", this.handle_visibility_change);
  },
  methods: {
    identity_name(identity) {
      if (!identity) return "";
      const name = `${identity.name_given} ${identity.name_family}`.trim();
      return name || identity.email || this.$t("session_changed.another_account");
    },
    receive_session_change(message) {
      this.latest_message = message;
      if (this.baseline_initialized) this.reconcile(message);
    },
    async reconcile(message = null) {
      const revision = ++this.reconcile_revision;
      const current = await fetchCurrentSession();
      if (revision !== this.reconcile_revision) return;

      let destination;
      if (current.known) {
        destination = current.session;
      } else if (message && message.after_known) {
        destination = message.after;
      } else if (message && message.transition === "login-pending") {
        this.notice = message;
        return;
      } else if (message && message.transition === "session-expired") {
        this.notice = message;
        return;
      } else {
        return;
      }

      if (sessionsMatch(this.baseline, destination)) {
        this.notice = null;
        return;
      }

      this.notice = {
        ...(message || {}),
        transition: this.transition_for(destination, message),
        before: this.baseline,
        after: destination,
        pending_login_email: message?.pending_login_email || null,
      };
    },
    transition_for(destination, message) {
      if (message?.transition === "login-pending") return "login-pending";
      if (destination === null) {
        return message?.transition === "session-expired" ? "session-expired" : "logout";
      }
      if (destination.masquerade) return "masquerade";
      if (this.baseline?.masquerade) return "unmasquerade";
      return "login";
    },
    handle_visibility_change() {
      if (document.visibilityState === "visible" && this.baseline_initialized) {
        this.reconcile(this.latest_message);
      }
    },
    reload() {
      window.location.reload();
    },
  },
};
</script>
